import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  User,
  X,
} from 'lucide-react';
import toast from '../utils/toast';
import Portal from './UI/Portal';
import ConfirmModal from './UI/ConfirmModal';
import LoadingSpinner from './UI/LoadingSpinner';
import MobileVerificationTabs from './UI/MobileVerificationTabs';
import VerificationPanelHeader from './UI/VerificationPanelHeader';
import DatePickerField from './UI/DatePickerField';
import PdfViewer from './UI/PdfViewer';
import PillToggle from './UI/PillToggle';
import { StatusBadge } from './UI/StatusBadge';
import { useIsMobile } from '../hooks/useIsMobile';
import { useIR21DirectiveDocumentBlob } from '../hooks/useOffboardingCases';
import { parseLocalDate, toLocalISODate } from '../utils/dateUtils';
import {
  DOCUMENT_TYPE_OPTIONS,
  OUTCOME_FROM_DOCUMENT_TYPE,
  mapOcrToForm,
  payrollEffectCopy,
} from '../utils/ir21DirectiveVerification';

const PREVIEW_TYPES = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

const documentTypeFromFilename = (filename = '', contentType = '') => {
  const ext = String(filename).split('.').pop()?.toLowerCase() || '';
  return PREVIEW_TYPES[ext] || contentType || 'application/octet-stream';
};

const IRASDirectiveVerificationModal = ({
  isOpen,
  onClose,
  workflowId,
  employeeName = '',
  document = null,
  alreadyDisbursed = false,
  onVerified,
}) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('document');
  const [form, setForm] = useState(() => mapOcrToForm(document));
  const [matchesDocument, setMatchesDocument] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const previewUrlRef = useRef('');

  const documentId = document?.id;
  const {
    data: blob,
    isLoading: blobLoading,
    isError: blobError,
  } = useIR21DirectiveDocumentBlob(isOpen ? workflowId : null, isOpen ? documentId : null);

  useEffect(() => {
    setForm(mapOcrToForm(document));
    setMatchesDocument(false);
    setActiveTab('document');
  }, [documentId]);

  useEffect(() => {
    if (!blob) return undefined;
    const type = documentTypeFromFilename(document?.original_filename, document?.content_type);
    const objectUrl = URL.createObjectURL(new Blob([blob], { type: type || blob.type || 'application/pdf' }));
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = objectUrl;
    setPreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
      if (previewUrlRef.current === objectUrl) previewUrlRef.current = '';
    };
  }, [blob, document?.original_filename, document?.content_type]);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setConfirmClose(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const warnings = document?.verification?.warnings || [];
  const ocrFailed = document?.verification_status === 'failed' || Boolean(document?.ocr_status_message);
  const manualReview = Boolean(document?.verification?.manual_review_required || ocrFailed);
  const previewKind = (document?.original_filename || '').toLowerCase().endsWith('.pdf')
    || (document?.content_type || '').includes('pdf')
    ? 'pdf'
    : 'image';
  const payroll = payrollEffectCopy({
    outcome: form.outcome,
    amount: form.amount,
    alreadyDisbursed,
  });
  const match = document?.match || {};
  const canVerify = Boolean(
    matchesDocument
    && (form.outcome === 'pay_tax' || form.outcome === 'release')
    && form.directiveDate
    && String(form.reference || '').trim()
    && (form.outcome !== 'pay_tax' || (form.amount !== '' && Number(form.amount) >= 0)),
  );

  const updateField = useCallback((key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  }, []);

  const handleDocumentType = (documentType) => {
    const outcome = OUTCOME_FROM_DOCUMENT_TYPE[documentType] || '';
    setForm((current) => ({
      ...current,
      documentType,
      outcome,
      amount: outcome === 'release' ? '0.00' : current.amount,
      dueDate: outcome === 'release' ? '' : current.dueDate,
    }));
  };

  const handleVerify = () => {
    if (!canVerify) {
      toast.error('Confirm the letter values before recording the outcome.');
      return;
    }
    onVerified?.({
      documentId,
      fileSha256: document?.file_sha256 || '',
      documentType: form.documentType,
      outcome: form.outcome,
      directiveDate: form.directiveDate,
      reference: String(form.reference || '').trim(),
      amount: form.outcome === 'pay_tax' ? form.amount : undefined,
      dueDate: form.outcome === 'pay_tax' ? form.dueDate : undefined,
      paymentSlipNumber: form.paymentSlipNumber || '',
      alreadyDisbursed,
    });
    toast.success('Values confirmed against the letter. Record the outcome on the case.');
    onClose?.();
  };

  const requestClose = () => setConfirmClose(true);
  const payrollToneClass = useMemo(() => {
    if (payroll.tone === 'blocking') return 'border-red-200 bg-red-50 text-red-800';
    if (payroll.tone === 'deduct') return 'border-orange-200 bg-orange-50 text-orange-800';
    if (payroll.tone === 'release') return 'border-green-200 bg-green-50 text-green-800';
    return 'border-gray-200 bg-gray-50 text-gray-700';
  }, [payroll.tone]);

  if (!isOpen || !document) return null;

  return (
    <Portal>
      <div
        className="modal-overlay"
        onClick={requestClose}
        role="presentation"
      >
        <div
          className={`modal-container max-w-7xl ${isMobile ? 'h-[calc(100dvh-1rem)]' : 'h-[92vh] max-h-[96vh]'} flex flex-col overflow-hidden`}
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="iras-directive-title"
        >
          <div className="modal-header">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <h3 id="iras-directive-title" className="modal-title truncate min-w-0">
                  IRAS letter verification
                </h3>
                <div className="flex-shrink-0">
                  <StatusBadge
                    status={document.verification_status || 'verification_required'}
                    showIcon
                  />
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-0.5 truncate">
                {employeeName || 'Employee'}
                {document.original_filename ? (
                  <>
                    <span className="mx-1.5 text-gray-400">·</span>
                    {document.original_filename}
                  </>
                ) : null}
              </p>
            </div>
            <button
              type="button"
              onClick={requestClose}
              className="modal-close-admin flex-shrink-0"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="modal-content !overflow-hidden !p-0 flex flex-col">
            <div className={`flex-1 flex ${isMobile ? 'flex-col' : 'flex-row'} overflow-hidden min-h-0`}>
              {isMobile && (
                <MobileVerificationTabs
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  documentLabel="Letter"
                  formLabel="Verification"
                  isAdmin
                />
              )}

              <div className={`${isMobile ? (activeTab === 'document' ? 'flex-1' : 'hidden') : 'w-2/3'} ${!isMobile ? 'border-r' : ''} border-gray-200 flex flex-col min-h-0 bg-gray-50`}>
                {!isMobile && (
                  <VerificationPanelHeader title="IRAS Letter" />
                )}
                {blobLoading && (
                  <div className="flex-1 flex items-center justify-center">
                    <LoadingSpinner />
                  </div>
                )}
                {blobError && (
                  <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                    <AlertTriangle className="w-8 h-8 text-red-500 mb-2" />
                    <p className="text-sm text-gray-700">The letter preview could not be loaded.</p>
                  </div>
                )}
                {!blobLoading && !blobError && previewUrl && previewKind === 'pdf' && (
                  <PdfViewer
                    src={previewUrl}
                    fileName={document.original_filename || 'iras_letter.pdf'}
                    title="IRAS letter"
                    theme="admin"
                  />
                )}
                {!blobLoading && !blobError && previewUrl && previewKind === 'image' && (
                  <div className="flex-1 overflow-auto p-4">
                    <img
                      src={previewUrl}
                      alt="IRAS letter"
                      className="max-w-full h-auto mx-auto rounded-lg shadow"
                    />
                  </div>
                )}
              </div>

              <div className={`${isMobile ? (activeTab === 'form' ? 'flex-1' : 'hidden') : 'w-1/3'} flex flex-col min-h-0 min-w-0 overflow-hidden`}>
                {!isMobile && (
                  <VerificationPanelHeader
                    title="Extracted values"
                    trailing={manualReview ? <StatusBadge status="pending" label="Manual review" /> : null}
                  />
                )}

                <div
                  className="flex-1 p-4 overflow-y-auto overflow-x-hidden overscroll-y-contain min-h-0 space-y-4"
                  style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}
                >
                  {(ocrFailed || warnings.length > 0) && (
                    <div className="border border-orange-200 bg-orange-50 rounded-xl p-3 space-y-2">
                      <div className="flex items-center gap-2 text-sm font-semibold text-orange-800">
                        <AlertTriangle className="w-4 h-4" />
                        {ocrFailed ? 'Enter the letter values manually' : 'Check these before confirming'}
                      </div>
                      {document.ocr_status_message ? (
                        <p className="text-sm text-orange-800">{document.ocr_status_message}</p>
                      ) : null}
                      {warnings.filter((warning) => warning !== document.ocr_status_message).map((warning) => (
                        <p key={warning} className="text-sm text-orange-800">{warning}</p>
                      ))}
                    </div>
                  )}

                  <div className="border border-gray-200 rounded-xl p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                      <User className="w-4 h-4" />
                      Employee matching
                    </div>
                    <p className="text-sm text-gray-700">
                      Case: {match.employee_name || employeeName || '—'}
                    </p>
                    <p className="text-sm text-gray-700">
                      Letter: {form.employeeName || '—'}
                      {form.employeeFin ? ` · FIN ${form.employeeFin}` : ''}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <StatusBadge
                        status={match.name_match ? 'verified' : 'warning'}
                        label={match.name_match ? 'Name matches' : 'Name check'}
                      />
                      <StatusBadge
                        status={match.fin_match ? 'verified' : 'warning'}
                        label={match.fin_match ? 'FIN matches' : 'FIN check'}
                      />
                    </div>
                  </div>

                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-2">Document type</span>
                    <PillToggle
                      theme="admin"
                      variant="text"
                      fullWidth
                      ariaLabel="IRAS letter type"
                      value={form.documentType}
                      onChange={handleDocumentType}
                      options={DOCUMENT_TYPE_OPTIONS.filter((option) => option.id !== 'unknown' || form.documentType === 'unknown')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="iras-letter-date">
                      Letter date
                    </label>
                    <DatePickerField
                      id="iras-letter-date"
                      theme="admin"
                      wrapperClassName="w-full"
                      selected={parseLocalDate(form.directiveDate)}
                      onChange={(value) => updateField('directiveDate', toLocalISODate(value))}
                      maxDate={new Date()}
                      placeholderText="Select date"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="iras-letter-reference">
                      Letter / tax reference
                    </label>
                    <input
                      id="iras-letter-reference"
                      type="text"
                      className="input-admin"
                      value={form.reference}
                      onChange={(event) => updateField('reference', event.target.value)}
                      placeholder="IRAS reference"
                    />
                  </div>

                  {form.outcome === 'pay_tax' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="iras-letter-amount">
                          Amount to IRAS
                        </label>
                        <input
                          id="iras-letter-amount"
                          type="number"
                          min="0"
                          step="0.01"
                          className="input-admin"
                          value={form.amount}
                          onChange={(event) => updateField('amount', event.target.value)}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="iras-letter-due">
                          Payment due
                        </label>
                        <DatePickerField
                          id="iras-letter-due"
                          theme="admin"
                          wrapperClassName="w-full"
                          selected={parseLocalDate(form.dueDate)}
                          onChange={(value) => updateField('dueDate', toLocalISODate(value))}
                          placeholderText="IRAS due date"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="iras-payment-slip">
                          Payment slip number
                        </label>
                        <input
                          id="iras-payment-slip"
                          type="text"
                          className="input-admin font-mono"
                          value={form.paymentSlipNumber}
                          onChange={(event) => updateField('paymentSlipNumber', event.target.value)}
                          placeholder="Optional"
                        />
                      </div>
                    </>
                  )}

                  <div className={`rounded-xl border p-3 text-sm ${payrollToneClass}`}>
                    <p className="font-semibold">{payroll.title}</p>
                    <p className="mt-1">{payroll.body}</p>
                  </div>

                  <label className="flex items-start gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={matchesDocument}
                      onChange={(event) => setMatchesDocument(event.target.checked)}
                    />
                    <span>I confirm these values match the IRAS letter.</span>
                  </label>
                </div>

                <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex-shrink-0 flex gap-3 btn-row-spread">
                  <button type="button" onClick={requestClose} className="btn btn-admin-outline">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleVerify}
                    disabled={!canVerify}
                    className="btn btn-admin-primary"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Values match the document
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmClose}
        theme="admin"
        title="Discard this review?"
        message="Closing now discards unsaved corrections. The uploaded letter stays on the case."
        confirmLabel="Discard review"
        onCancel={() => setConfirmClose(false)}
        onConfirm={() => {
          setConfirmClose(false);
          onClose?.();
        }}
      />
    </Portal>
  );
};

export default IRASDirectiveVerificationModal;
